/**
 * Uncanned analytics abstraction.
 *
 * One place that knows how to describe a product, and one place that knows which
 * destinations exist. Sections call `Uncanned.track.addToCart(payload)` and never
 * touch `dataLayer`, `gtag` or `fbq` directly — so a destination can be added or
 * removed without touching a single section again.
 *
 * Destinations are opt-in and silently skipped when absent, so nothing here breaks
 * a page when GA4 or the Meta pixel has not been installed yet.
 *
 * Double-firing is guarded two ways:
 *   - `view_item` / `view_item_list` fire at most once per page per list.
 *   - Every payload carries a dedupe key; identical events inside 800ms are dropped.
 *     (Real duplicate intent, e.g. tapping add-to-cart twice, is not: the key includes
 *      a monotonic counter for interaction events.)
 */
(function () {
  'use strict';

  var config = window.UncannedConfig || {};
  var currency = config.currency || 'INR';
  var debug = !!config.debug;

  window.dataLayer = window.dataLayer || [];

  var seen = Object.create(null);
  var recent = Object.create(null);

  function log() {
    if (!debug) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['%c[uncanned]', 'color:#b25f38;font-weight:bold'].concat(args));
  }

  function isDuplicate(key) {
    var now = Date.now();
    if (recent[key] && now - recent[key] < 800) return true;
    recent[key] = now;
    return false;
  }

  /** Money in this codebase travels as paise. GA4 wants major units. */
  function toMajor(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return 0;
    return Math.round(n) / 100;
  }

  /**
   * Normalise anything product-shaped into a GA4 item.
   * Accepts either a Shopify line item, a variant, or a hand-built object.
   */
  function toItem(raw, index) {
    if (!raw) return null;
    var price = raw.price != null ? raw.price : raw.final_price;
    var item = {
      item_id: String(raw.sku || raw.variant_id || raw.id || ''),
      item_name: raw.product_title || raw.title || raw.name || '',
      item_brand: 'Uncanned',
      price: toMajor(price),
      quantity: Number(raw.quantity) || 1,
      currency: currency,
    };
    if (raw.variant_title || raw.variant) item.item_variant = raw.variant_title || raw.variant;
    if (raw.product_type || raw.type) item.item_category = raw.product_type || raw.type;
    if (raw.list_name) item.item_list_name = raw.list_name;
    if (typeof index === 'number') item.index = index;
    return item;
  }

  function toItems(input) {
    if (!input) return [];
    var arr = Array.isArray(input) ? input : [input];
    return arr
      .map(function (raw, i) {
        return toItem(raw, arr.length > 1 ? i : undefined);
      })
      .filter(Boolean);
  }

  function itemsValue(items) {
    return Number(
      items
        .reduce(function (sum, i) {
          return sum + i.price * (i.quantity || 1);
        }, 0)
        .toFixed(2)
    );
  }

  /* ---------------------------------------------------------------- dispatch */

  function toGA4(name, params) {
    // GA4 via GTM reads dataLayer; GA4 via gtag.js reads the function. Support both,
    // but never both at once — that is the classic source of doubled events.
    if (typeof window.gtag === 'function' && !config.useGtmDataLayer) {
      window.gtag('event', name, params);
      return;
    }
    // GTM convention: clear the previous ecommerce object before pushing a new one,
    // otherwise keys from the last event leak into this one. The clearing push
    // carries no `event` key — naming it here would double-count every event.
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(Object.assign({ event: name }, params));
  }

  var META_MAP = {
    view_item: 'ViewContent',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    purchase: 'Purchase',
    search: 'Search',
  };

  function toMeta(name, params) {
    if (typeof window.fbq !== 'function') return;
    var metaName = META_MAP[name];
    if (!metaName) return;
    var items = (params.ecommerce && params.ecommerce.items) || [];
    window.fbq('track', metaName, {
      content_type: 'product',
      content_ids: items.map(function (i) {
        return i.item_id;
      }),
      contents: items.map(function (i) {
        return { id: i.item_id, quantity: i.quantity };
      }),
      value: params.ecommerce ? params.ecommerce.value : undefined,
      currency: currency,
      search_string: params.search_term,
    });
  }

  function emit(name, params, dedupeKey) {
    if (dedupeKey && isDuplicate(name + '|' + dedupeKey)) {
      log('deduped', name, dedupeKey);
      return;
    }
    log(name, params);
    toGA4(name, params);
    toMeta(name, params);
    // Anything else on the page (Shopify Web Pixels, a heatmap tool, a test harness)
    // can subscribe without us knowing about it.
    document.dispatchEvent(
      new CustomEvent('uncanned:analytics', { detail: { name: name, params: params } })
    );
  }

  function ecommerce(name, items, extra, dedupeKey) {
    var normalised = toItems(items);
    var params = Object.assign(
      {
        ecommerce: Object.assign(
          {
            currency: currency,
            value: itemsValue(normalised),
            items: normalised,
          },
          extra || {}
        ),
      },
      extra && extra.top ? extra.top : {}
    );
    emit(name, params, dedupeKey);
  }

  /* ------------------------------------------------------------------ public */

  var track = {
    /** Fires once per product per page. */
    viewItem: function (product) {
      var key = 'view_item:' + (product && (product.id || product.variant_id));
      if (seen[key]) return;
      seen[key] = true;
      ecommerce('view_item', product);
    },

    /** Fires once per named list per page. */
    viewItemList: function (listName, products) {
      var key = 'view_item_list:' + listName;
      if (seen[key]) return;
      seen[key] = true;
      ecommerce('view_item_list', products, { item_list_name: listName });
    },

    selectItem: function (listName, product) {
      ecommerce('select_item', product, { item_list_name: listName });
    },

    addToCart: function (product, meta) {
      ecommerce('add_to_cart', product, null, 'atc:' + JSON.stringify(meta || {}));
    },

    removeFromCart: function (product) {
      ecommerce('remove_from_cart', product);
    },

    viewCart: function (items) {
      ecommerce('view_cart', items);
    },

    beginCheckout: function (items) {
      ecommerce('begin_checkout', items);
    },

    search: function (term, resultCount) {
      emit('search', { search_term: term, result_count: resultCount }, 'search:' + term);
    },

    viewPromotion: function (promotion) {
      var key = 'view_promotion:' + promotion.promotion_name;
      if (seen[key]) return;
      seen[key] = true;
      emit('view_promotion', { ecommerce: promotion });
    },

    selectPromotion: function (promotion) {
      emit('select_promotion', { ecommerce: promotion });
    },

    /**
     * Behavioural CRO events. Deliberately a separate, flat namespace so they never
     * pollute the ecommerce funnel reports.
     * Naming convention: `unc_<object>_<verb>`, snake_case, always past tense-free.
     */
    interaction: function (name, detail) {
      emit('unc_' + name, detail || {});
    },
  };

  window.Uncanned = window.Uncanned || {};
  window.Uncanned.track = track;
  window.Uncanned.toItems = toItems;
  window.Uncanned.currency = currency;

  /* ------------------------------------------------- declarative attachment */

  /**
   * Anything with `data-unc-event="hero_cta_click"` is tracked on click without the
   * section needing its own JS. Extra params come from `data-unc-event-*` attributes.
   */
  document.addEventListener(
    'click',
    function (event) {
      var el = event.target.closest && event.target.closest('[data-unc-event]');
      if (!el) return;
      var detail = {};
      Object.keys(el.dataset).forEach(function (key) {
        if (key.indexOf('uncEvent') === 0 && key !== 'uncEvent') {
          var name = key.slice(8);
          detail[name.charAt(0).toLowerCase() + name.slice(1)] = el.dataset[key];
        }
      });
      track.interaction(el.dataset.uncEvent, detail);
    },
    { capture: true }
  );

  /** Checkout hand-off, wherever it happens. */
  document.addEventListener(
    'click',
    function (event) {
      var el = event.target.closest && event.target.closest('[name="checkout"], .unc-checkout-cta');
      if (!el) return;
      var cart = window.Uncanned.cartSnapshot;
      track.beginCheckout(cart ? cart.items : []);
    },
    { capture: true }
  );
})();
